import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { availableBaseReports, getReportDisplayName } from "./customReportConfig";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { APIURL } from "../../config";
import CustomReportBuilder from "./CustomReportBuilder";
import CustomReportViewer from "./CustomReportViewer";


export default function CustomReportsManager(props) {
    const [customReports, setCustomReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showBuilder, setShowBuilder] = useState(false);
    const [showViewer, setShowViewer] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [successText, setSuccessText] = useState("");
    const [errorText, setErrorText] = useState("");

    const fetchCustomReports = React.useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${APIURL}/reports/custom`, {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id
                }
            });
            // Ensure we always set an array
            const data = response.data;
            setCustomReports(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching custom reports:", err);
            setErrorText("Failed to load custom reports");
            setCustomReports([]); // Set empty array on error
        } finally {
            setIsLoading(false);
        }
    }, [props.user.comp_id, props.user.emp_id]);

    useEffect(() => {
        fetchCustomReports();
    }, [fetchCustomReports]);

    const handleCreateReport = () => {
        setSelectedReport(null);
        setShowBuilder(true);
    };

    const handleEditReport = (report) => {
        setSelectedReport(report);
        setShowBuilder(true);
    };

    const handleViewReport = (report) => {
        setSelectedReport(report);
        setShowViewer(true);
    };

    const handleDeleteClick = (report) => {
        setReportToDelete(report);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(`${APIURL}/reports/custom/${reportToDelete.id}`);
            setSuccessText("Custom report deleted successfully");
            fetchCustomReports();
        } catch (err) {
            console.error("Error deleting custom report:", err);
            setErrorText("Failed to delete custom report");
        } finally {
            setDeleteDialogOpen(false);
            setReportToDelete(null);
        }
    };

    const handleBuilderClose = (refresh) => {
        setShowBuilder(false);
        setSelectedReport(null);
        if (refresh) {
            fetchCustomReports();
        }
    };

    const handleViewerClose = () => {
        setShowViewer(false);
        setSelectedReport(null);
    };

    return (
        <Box sx={{ width: "100%", pl: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" fontWeight="bold" fontFamily="Lato">
                    Custom Reports
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateReport}
                    sx={{ backgroundColor: "#3fadf6" }}
                >
                    Create Custom Report
                </Button>
            </Box>

            <Paper elevation={2} sx={{ mt: 2 }}>
                {isLoading ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography>Loading custom reports...</Typography>
                    </Box>
                ) : customReports.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography color="textSecondary">
                            No custom reports created yet. Click "Create Custom Report" to get started.
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {customReports.map((report, index) => (
                            <React.Fragment key={report.id}>
                                {index > 0 && <Divider />}
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {report.report_name}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="textSecondary">
                                                Based on: {getReportDisplayName(report.base_report)}
                                            </Typography>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            aria-label="view"
                                            onClick={() => handleViewReport(report)}
                                            sx={{ mr: 1 }}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => handleEditReport(report)}
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => handleDeleteClick(report)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Custom Report</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{reportToDelete?.report_name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Report Builder Modal */}
            {showBuilder && (
                <CustomReportBuilder
                    open={showBuilder}
                    onClose={handleBuilderClose}
                    user={props.user}
                    report={selectedReport}
                    availableBaseReports={availableBaseReports}
                    setSuccessText={setSuccessText}
                    setErrorText={setErrorText}
                />
            )}

            {/* Report Viewer Modal */}
            {showViewer && selectedReport && (
                <CustomReportViewer
                    open={showViewer}
                    onClose={handleViewerClose}
                    user={props.user}
                    report={selectedReport}
                    setSuccessText={setSuccessText}
                    setErrorText={setErrorText}
                />
            )}

            {/* Success Snackbar */}
            <Snackbar
                open={successText !== ""}
                autoHideDuration={5000}
                onClose={() => setSuccessText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity="success"
                    elevation={6}
                    variant="filled"
                >
                    {successText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setSuccessText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={errorText !== ""}
                autoHideDuration={5000}
                onClose={() => setErrorText("")}
            >
                <Alert
                    sx={{ width: "100%" }}
                    severity="error"
                    elevation={6}
                    variant="filled"
                >
                    {errorText}
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setErrorText("")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Alert>
            </Snackbar>
        </Box>
    );
}
